import {
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  type MutableRefObject,
} from 'react';
import { LogLevel, useSeqLog } from '@/hooks/telemetry/use-seq-log';
import { ModalStackContext } from './modal-stack-context';
import {
  createModalRegistrationResult,
  createOpenModalRegistration,
  getBackdropRegistrationDependencies,
  getModalRegistrationContextParts,
  getModalRegistrationLayer,
  getModalVisibilityAction,
  isTopOpenModal,
  ModalVisibilityAction,
  normalizeUseRegisterModalInput,
} from './modal-registration-helpers';
import {
  type ModalBackdropConfig,
  type ModalRegistration,
  type ModalStackContextValue,
  type UseRegisterModalInput,
} from './modal-stack-types';

const REF_CLICK_DEBUG_FLOW = 'ref-click-debug';
const URM_LOG_COMPONENT_NAME = 'useRegisterModal';

type ModalRegistrationLogger = ReturnType<typeof useSeqLog>;

interface LatestModalRegistrationRefsInput {
  onClose: () => void;
  backdrop: ModalBackdropConfig | null;
  visible: boolean;
}

interface LatestModalRegistrationRefs {
  onCloseRef: MutableRefObject<() => void>;
  backdropRef: MutableRefObject<ModalBackdropConfig | null>;
  visibleRef: MutableRefObject<boolean>;
}

interface ModalRegistrationEffectInput {
  id: string;
  visible: boolean;
  holdUntilExitComplete: boolean;
  backdrop: ModalBackdropConfig | null;
  onCloseRef: MutableRefObject<() => void>;
  backdropRef: MutableRefObject<ModalBackdropConfig | null>;
  registeredIdRef: MutableRefObject<string | null>;
  registerModal?: ModalStackContextValue['registerModal'];
  markClosing?: ModalStackContextValue['markClosing'];
  unregisterModal?: ModalStackContextValue['unregisterModal'];
  refClickLog: ModalRegistrationLogger;
}

interface ModalUnmountEffectInput {
  registeredIdRef: MutableRefObject<string | null>;
  setupGenerationRef: MutableRefObject<number>;
  unregisterModal?: ModalStackContextValue['unregisterModal'];
  refClickLog: ModalRegistrationLogger;
}

interface ModalCompleteExitInput {
  registeredIdRef: MutableRefObject<string | null>;
  unregisterModal?: ModalStackContextValue['unregisterModal'];
  visibleRef: MutableRefObject<boolean>;
}

/** True iff `registrationId` is the topmost Open entry in the stack. */
export function useIsTopModal(registrationId: string): boolean {
  const context = useContext(ModalStackContext);
  return isTopOpenModal({ id: registrationId, stack: context?.stack });
}

export function useRegisterModal(
  input: UseRegisterModalInput,
): ModalRegistration {
  const { id, visible, onClose, backdrop, holdUntilExitComplete } =
    normalizeUseRegisterModalInput(input);
  const contextParts = getModalRegistrationContextParts(
    useContext(ModalStackContext),
  );
  const { onCloseRef, backdropRef, visibleRef } =
    useLatestModalRegistrationRefs({
      onClose,
      backdrop,
      visible,
    });
  // Ownership is per hook instance: hidden siblings must never close or remove
  // a registration that belongs to another mounted modal.
  const registeredIdRef = useRef<string | null>(null);
  // A newer effect setup vetoes the deferred cleanup from StrictMode replay or
  // an ordinary dependency change; only a real unmount reaches unregister.
  const setupGenerationRef = useRef(0);
  const refClickLog = useSeqLog(URM_LOG_COMPONENT_NAME, {
    flow: REF_CLICK_DEBUG_FLOW,
  });

  useModalRegistrationEffect({
    id,
    visible,
    holdUntilExitComplete,
    backdrop,
    onCloseRef,
    backdropRef,
    registeredIdRef,
    registerModal: contextParts.registerModal,
    markClosing: contextParts.markClosing,
    unregisterModal: contextParts.unregisterModal,
    refClickLog,
  });

  useModalUnmountEffect({
    registeredIdRef,
    setupGenerationRef,
    unregisterModal: contextParts.unregisterModal,
    refClickLog,
  });

  const completeExit = useModalCompleteExit({
    registeredIdRef,
    unregisterModal: contextParts.unregisterModal,
    visibleRef,
  });
  const layer = getModalRegistrationLayer({ id, stack: contextParts.stack });
  const isTopOpen = isTopOpenModal({ id, stack: contextParts.stack });

  return createModalRegistrationResult({
    layer,
    isTopOpen,
    completeExit,
  });
}

function useLatestModalRegistrationRefs(
  input: LatestModalRegistrationRefsInput,
): LatestModalRegistrationRefs {
  const onCloseRef = useRef(input.onClose);
  const backdropRef = useRef(input.backdrop);
  const visibleRef = useRef(input.visible);

  useLayoutEffect(() => {
    onCloseRef.current = input.onClose;
    backdropRef.current = input.backdrop;
    visibleRef.current = input.visible;
  });

  return { onCloseRef, backdropRef, visibleRef };
}

function useModalRegistrationEffect(input: ModalRegistrationEffectInput): void {
  const {
    id,
    visible,
    holdUntilExitComplete,
    backdrop,
    onCloseRef,
    backdropRef,
    registeredIdRef,
    registerModal,
    markClosing,
    unregisterModal,
    refClickLog,
  } = input;
  const [
    backdropOpacity,
    backdropBlur,
    backdropUseGradient,
    backdropTopOffset,
    backdropAnimationDuration,
    backdropStyle,
  ] = getBackdropRegistrationDependencies(backdrop);

  useLayoutEffect(() => {
    if (!registerModal || !markClosing || !unregisterModal) {
      return;
    }
    const actionHandlers: Record<ModalVisibilityAction, () => void> = {
      [ModalVisibilityAction.RegisterOpen]: () => {
        refClickLog(
          'URM-2 useRegisterModal: visible=true → registerModal(Open)',
          { id },
          LogLevel.Warning,
        );
        registerModal(
          createOpenModalRegistration({
            id,
            onCloseRef,
            backdropRef,
          }),
        );
        const previousRegisteredId = registeredIdRef.current;
        registeredIdRef.current = id;
        // Register first so an identity change cannot expose the page between
        // removing the old entry and publishing its replacement.
        if (previousRegisteredId && previousRegisteredId !== id) {
          unregisterModal(previousRegisteredId);
        }
      },
      [ModalVisibilityAction.MarkClosing]: () => {
        const registeredId = registeredIdRef.current;
        if (!registeredId) return;
        refClickLog(
          'URM-1 useRegisterModal: visible=false → markClosing',
          { id: registeredId },
          LogLevel.Warning,
        );
        markClosing(registeredId);
      },
      [ModalVisibilityAction.Unregister]: () => {
        const registeredId = registeredIdRef.current;
        if (!registeredId) return;
        registeredIdRef.current = null;
        refClickLog(
          'URM-1 useRegisterModal: visible=false → unregisterModal',
          { id: registeredId },
          LogLevel.Warning,
        );
        unregisterModal(registeredId);
      },
    };
    const action = getModalVisibilityAction({
      visible,
      holdUntilExitComplete,
    });
    actionHandlers[action]();
  }, [
    registerModal,
    markClosing,
    unregisterModal,
    id,
    visible,
    holdUntilExitComplete,
    refClickLog,
    onCloseRef,
    backdropRef,
    registeredIdRef,
    backdropOpacity,
    backdropBlur,
    backdropUseGradient,
    backdropTopOffset,
    backdropAnimationDuration,
    backdropStyle,
  ]);
}

function useModalUnmountEffect(input: ModalUnmountEffectInput): void {
  const { registeredIdRef, setupGenerationRef, unregisterModal, refClickLog } =
    input;

  useLayoutEffect(() => {
    if (!unregisterModal) return undefined;
    setupGenerationRef.current += 1;
    const setupGeneration = setupGenerationRef.current;

    return () => {
      // React development replay immediately runs a newer setup. Deferring one
      // task lets that setup invalidate this cleanup before stack state changes.
      window.setTimeout(() => {
        if (setupGenerationRef.current !== setupGeneration) return;
        const registeredId = registeredIdRef.current;
        if (!registeredId) return;
        registeredIdRef.current = null;
        refClickLog(
          'URM-3 useRegisterModal unmount → unregisterModal',
          { id: registeredId },
          LogLevel.Warning,
        );
        unregisterModal(registeredId);
      }, 0);
    };
  }, [unregisterModal, refClickLog, registeredIdRef, setupGenerationRef]);
}

function useModalCompleteExit(input: ModalCompleteExitInput): () => void {
  const { registeredIdRef, unregisterModal, visibleRef } = input;

  return useCallback(() => {
    // AnimatePresence may finish an old exit after a rapid reopen.
    if (visibleRef.current) return;
    const registeredId = registeredIdRef.current;
    if (!registeredId) return;
    registeredIdRef.current = null;
    unregisterModal?.(registeredId);
  }, [registeredIdRef, unregisterModal, visibleRef]);
}
